(() => {
  'use strict';

  if (customElements.get('lm-navigation')) return;

  class LmNavigation extends HTMLElement {
    connectedCallback() {
      this._cleanup = [];
      this._openItem = null;
      this._closeTimer = null;
      this._resizeFrame = null;
      this._suppressFocusOpen = false;
      this._initializationFrame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this._initializationFrame);
      cancelAnimationFrame(this._resizeFrame);
      clearTimeout(this._closeTimer);
      this._resizeObserver?.disconnect();
      this._cleanup?.forEach((cleanup) => cleanup());
      this._cleanup = [];
      this.removeAttribute('data-lm-initialized');
    }

    initialize() {
      if (this.hasAttribute('data-lm-initialized')) return;

      const items = [...this.querySelectorAll(':scope > .lm-navigation__list > .lm-navigation__item')];
      if (!items.length) return;

      this.setAttribute('data-lm-initialized', 'true');

      items.forEach((item) => this.bindItem(item));
      this.bind(this, 'keydown', (event) => this.handleKeydown(event));
      this.bind(document, 'pointerdown', (event) => {
        if (!this.contains(event.target)) this.closeMenu();
      });
      this.bind(window, 'resize', () => this.scheduleOverlayUpdate(), { passive: true });

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this.scheduleOverlayUpdate());
      }
    }

    bindItem(item) {
      const menu = item.querySelector(':scope > [data-lm-mega-menu]');
      const trigger = item.querySelector(':scope > .lm-navigation__top-level > [data-lm-menu-trigger]');

      this.bind(item, 'mouseenter', () => {
        clearTimeout(this._closeTimer);
        if (menu) this.openMenu(item);
        else this.closeMenu();
      });

      this.bind(item, 'mouseleave', () => {
        this._closeTimer = window.setTimeout(() => this.closeMenu(item), 120);
      });

      this.bind(item, 'focusin', () => {
        if (this._suppressFocusOpen) return;
        clearTimeout(this._closeTimer);
        if (menu) this.openMenu(item);
      });

      this.bind(item, 'focusout', (event) => {
        if (!item.contains(event.relatedTarget)) {
          this._closeTimer = window.setTimeout(() => this.closeMenu(item), 0);
        }
      });

      if (!menu || !trigger) return;

      const tabs = [...menu.querySelectorAll('[data-lm-tab]')];
      tabs.forEach((tab) => {
        this.bind(tab, 'mouseenter', () => this.activateTab(menu, tab.dataset.lmTab));
        this.bind(tab, 'click', () => this.activateTab(menu, tab.dataset.lmTab));
        this.bind(tab, 'keydown', (event) => this.handleTabKeydown(event, menu, tabs));
      });
    }

    openMenu(item, focusTab = false) {
      if (this._openItem && this._openItem !== item) this.closeMenu(this._openItem);

      const menu = item.querySelector(':scope > [data-lm-mega-menu]');
      const trigger = item.querySelector('[data-lm-menu-trigger]');
      if (!menu || !trigger) return;

      clearTimeout(this._closeTimer);
      this._openItem = item;
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      menu.setAttribute('aria-hidden', 'false');
      menu.inert = false;
      this.classList.add('is-overlay-open');

      const activeTab = menu.querySelector('[role="tab"][aria-selected="true"]');
      if (activeTab) {
        this.activateTab(menu, activeTab.dataset.lmTab, focusTab);
      }

      this._resizeObserver?.disconnect();
      this._resizeObserver?.observe(menu);
      this.scheduleOverlayUpdate();
    }

    closeMenu(item = this._openItem, returnFocus = false) {
      if (!item || (this._openItem && item !== this._openItem)) return;

      const menu = item.querySelector(':scope > [data-lm-mega-menu]');
      const trigger = item.querySelector('[data-lm-menu-trigger]');

      item.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
      menu?.setAttribute('aria-hidden', 'true');
      if (menu) menu.inert = true;
      this.classList.remove('is-overlay-open');
      this.style.setProperty('--lm-overlay-height', '0px');
      this._resizeObserver?.disconnect();
      this._openItem = null;

      if (returnFocus && trigger) {
        this._suppressFocusOpen = true;
        trigger.focus();
        queueMicrotask(() => {
          this._suppressFocusOpen = false;
        });
      }
    }

    activateTab(menu, index, focus = false) {
      const tabs = [...menu.querySelectorAll('[data-lm-tab]')];
      const panels = [...menu.querySelectorAll('[data-lm-panel]')];
      const activeTab = tabs.find((tab) => tab.dataset.lmTab === String(index));
      const activePanel = panels.find((panel) => panel.dataset.lmPanel === String(index));
      if (!activeTab || !activePanel) return;

      tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      panels.forEach((panel) => {
        const isActive = panel === activePanel;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      this.hydrateProducts(activePanel);
      if (focus) activeTab.focus();
      this.scheduleOverlayUpdate();
    }

    hydrateProducts(panel) {
      const template = panel.querySelector('[data-lm-products-template]');
      const mount = panel.querySelector('[data-lm-products-mount]');
      if (!template || !mount || mount.hasAttribute('data-lm-hydrated')) return;

      mount.append(template.content.cloneNode(true));
      mount.setAttribute('data-lm-hydrated', 'true');
    }

    handleTabKeydown(event, menu, tabs) {
      const currentIndex = tabs.indexOf(event.currentTarget);
      let targetIndex = null;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = tabs.length - 1;
      if (targetIndex === null) return;

      event.preventDefault();
      this.activateTab(menu, tabs[targetIndex].dataset.lmTab, true);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || !this._openItem) return;
      event.preventDefault();
      this.closeMenu(this._openItem, true);
    }

    scheduleOverlayUpdate() {
      if (!this._openItem || this._resizeFrame) return;

      this._resizeFrame = requestAnimationFrame(() => {
        this._resizeFrame = null;
        const menu = this._openItem?.querySelector(':scope > [data-lm-mega-menu]');
        if (!menu) return;

        const availableHeight = Math.max(0, window.innerHeight - menu.getBoundingClientRect().top);
        const overlayHeight = Math.min(menu.offsetHeight + 64, availableHeight);
        this.style.setProperty('--lm-overlay-height', `${overlayHeight}px`);
      });
    }

    bind(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this._cleanup.push(() => target.removeEventListener(type, handler, options));
    }
  }

  customElements.define('lm-navigation', LmNavigation);
})();
